/**
 * The must-change-password screen — design U8 item 11, D16
 * (`docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 and §5; owner "go" 2026-09-25).
 *
 * Shown when the session was minted with the `mc` claim: the password is older than the
 * ruled maximum (90 days by default), or an administrator typed it (create, reset). The
 * API admits only `PATCH /me/password`, `POST /auth/logout` and `GET /me` until it is
 * changed, and this is the screen that explains the session the person can hold but not
 * yet use — wired the way the premises-blocked screen is: the session provider renders it
 * INSTEAD of the app, so nothing under the router can fire a request that would be
 * refused 403 `PASSWORD_CHANGE_REQUIRED`.
 *
 * ===========================================================================
 * THE SETTINGS PASSWORD FORM, FULL-SCREEN — not a second form
 * ===========================================================================
 * The form is `features/settings/PasswordRow.tsx` in its `forced` mode: the same
 * component the Settings page renders, opened at once, without Cancel, and handing the
 * accepted password back so the provider can sign the person straight in with it
 * (`AuthService.changePassword` revokes every refresh token, so a re-login is the only
 * way forward). The design says "web and mobile route to the change screen"; in this tree
 * the change screen lives under `/countries/:cc/settings`, behind the country picker and
 * a shell that fires queries on mount — every one of which the gate would refuse. Rendering
 * the same form here, above the router, is the route that cannot loop.
 *
 * ONE SENTENCE OF WHY, NO ACCOUNT DETAIL: not which arm minted the claim, not the age,
 * not who set the password. The way out is the form, or Sign out.
 */

import { EntryBackdrop, G1Mark } from '../components/EntryBackdrop';
import { Button } from '../components/ui';
import { PasswordRow } from '../features/settings/PasswordRow';

export function PasswordChangeRequiredScreen({
  onChanged,
  onSignOut,
}: {
  /** Receives the accepted new password once; the provider re-signs the person in with it. */
  onChanged: (newPassword: string) => Promise<void>;
  onSignOut: () => void;
}): React.JSX.Element {
  return (
    <EntryBackdrop>
      <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-5 text-center">
        <G1Mark />
        <h1 className="text-h1 text-fg-inverse">Choose a new password before you continue</h1>
        <p className="text-body13 text-fg-inverse/70">
          Your password must be changed before this account can be used. Set one only you
          know, and you will be signed straight in.
        </p>
        <div className="w-full rounded-lg border border-fg-inverse/50 bg-surface p-5 text-left">
          <PasswordRow forced onChanged={onChanged} />
        </div>
        <Button variant="ghost-on-dark" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </EntryBackdrop>
  );
}
